// @import dependencies
import React, { CSSProperties, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Spinner } from 'react-bootstrap'
import {MdDeleteForever} from 'react-icons/md'
// @end dependencies

// @import components
// @end components

// @import types
// @end types

// @import components
// @end components

// @import services
// @end services

// @import hooks
import {useColors} from 'app_hooks/useColors'
// @end hooks

// @import actions
// @end actions

// @import utils
// @end utils

// @import assets
// @end assets

// @import styles
import './CourseSchedulingAssociate.scss'
import CourseSchedulingAssociationService from 'app_services/admin/courseScheduling/courseSchedulingAssociationService'
import { ICourseSchedulingAssociation, IListAssociations, ISchedulingAssociation } from 'app_types/admin/courseScheduling/courseSchedulingAssociationTypes'
import { BsPlusCircleFill } from 'react-icons/bs'
import { ScreenNotificationsUtil } from 'app_utils/screenNotification'
// @end styles

interface ICourseSchedulingAssociateProps {
  className?: string
  style?: CSSProperties
  id?: string
  onHandleSubmit?: (data: any) => void,
  courseSchedulingId: string
  enabledAddAssociationForm?: boolean
  enabledAddAssociation?: boolean
}

const uuid = require('react-uuid')

const CourseSchedulingAssociate: React.FC<ICourseSchedulingAssociateProps> = (props) => {

  const screenNotificationsUtil: ScreenNotificationsUtil = new ScreenNotificationsUtil();
  const [colors] = useColors()

  const courseSchedulingAssociateService: CourseSchedulingAssociationService = new CourseSchedulingAssociationService()

  const { t } = useTranslation()

  const formHook = useForm()

  const [enabledAddAssociationForm, updateEnabledAddAssociationForm] = useState<boolean>(props?.enabledAddAssociationForm !== undefined ? props?.enabledAddAssociationForm : true)
  const [enabledAddAssociation, updateEnabledAddAssociation] = useState<boolean>(props?.enabledAddAssociation !== undefined ? props?.enabledAddAssociation : true)
  const [isLoading, updateIsLoading] = useState<boolean>(false)
  const [customErrors, updateCustomErrors] = useState<{message: string}[]>([])
  const [masterCourseScheduling, updateMasterCourseScheduling] = useState<{
    status: 'success' | 'loading' | 'error',
    courseScheduling: ICourseSchedulingAssociation | null
  }>({
    status: 'loading',
    courseScheduling: null
  })
  const [listAssociations, updateListAssociations] = useState<IListAssociations[]>([])
  const [listAssociationsInQueue, updateListAssociationsInQueue] = useState<ISchedulingAssociation[]>([])

  const clearErrors = () => {
    updateCustomErrors([])
  }

  const handleSubmit = async (data: any) => {
    updateIsLoading(true)
    clearErrors()
    if (masterCourseScheduling.courseScheduling?._id) {
      const associations = data.associations || []
      if (associations.length === 0) {
        updateCustomErrors([{message: 'Es necesario agregar al menos una asociación'}])
        updateIsLoading(false)
        return
      }
      const submitResponse = await courseSchedulingAssociateService.associateSchedules(
        masterCourseScheduling.courseScheduling?._id,
        {
          masterGroup: data.masterGroup,
          associations: listAssociations
        }
      )
      if (submitResponse.status === 'error') {
        updateIsLoading(false)
        screenNotificationsUtil.fireToast(submitResponse)
      } else if (submitResponse.status === 'success') {
        if (submitResponse.associationErrors && submitResponse.associationErrors.length > 0) {
          updateCustomErrors(submitResponse.associationErrors)
          updateIsLoading(false)
        } else {
          screenNotificationsUtil.fireToast({
            ...submitResponse,
            message: 'Se han actualizado las asociaciones'
          })
          updateIsLoading(false)
          if (props.onHandleSubmit) {
            props.onHandleSubmit(data)
          }
        }
      }
    }
  }

  useEffect(() => {
    if (props.courseSchedulingId) {
      fetchAssociations(props.courseSchedulingId)
    }
  }, [props.courseSchedulingId])

  const fetchAssociations = async (courseSchedulingId: string) => {
    updateMasterCourseScheduling({
      status: 'loading',
      courseScheduling: null
    })
    const response = await courseSchedulingAssociateService.list(courseSchedulingId, {})
    if (response?.status === 'success') {
      if (response?.courseScheduling) {
        updateMasterCourseScheduling({
          status: 'success',
          courseScheduling: response?.courseScheduling
        })
      } else {
        updateMasterCourseScheduling({
          status: 'error',
          courseScheduling: null
        })
      }
      if (response?.schedulings) {
        const list: IListAssociations[] = response?.schedulings.reduce((accum: IListAssociations[], element) => {
          accum.push({
            _id: element._id,
            slug: element?.schedulingAssociation?.slug,
            serviceId: element?.metadata?.service_id
          })
          return accum;
        }, [])
        updateListAssociations(list)
      }
      if (response?.associationQueue) {
        const list: ISchedulingAssociation[] = response?.associationQueue.reduce((accum: ISchedulingAssociation[], element) => {
          accum.push({
            ...element
          })
          return accum;
        }, [])
        updateListAssociationsInQueue(list)
      }
    }
  }

  const handleAddAssociation = () => {
    clearErrors()
    const list: IListAssociations[] = [...listAssociations]
    list.push({
      unique: uuid(),
      slug: ''
    })
    updateListAssociations(list)
  }

  const handleRemoveAssociation = (index: string) => {
    clearErrors()
    const list: IListAssociations[] = listAssociations.filter((association) => {
      if (association._id) {
        return index !== association._id
      } else if (association.unique) {
        return index !== association.unique
      }
    })
    updateListAssociations(list)
  }

  const renderAssociation = (association: IListAssociations) => {
    const key = association?._id || association?.unique || ''
    const serviceId = association?.serviceId || undefined
    return (
      <div className='col-md-12 association-item' key={`association-container-${key}`}>
        <div className="form-group">
          <label className='association-label' htmlFor={`association_${key}`}>
            <div className='association-label-name' >
              {t('modules.courseSchedulingAssociation.associationGroup')}
              {serviceId ? ` (${serviceId})` : null}
            </div>
            {association?.unique && (
              <div className='association-label-actions'>
                <MdDeleteForever
                  size={'20px'}
                  color={colors.primary}
                  onClick={() => handleRemoveAssociation(key)}
                  className='icon-scheduling-screen'
                />
              </div>
            )}
          </label>
          <input
            type="text"
            name={`associations[${key}]`}
            id={`association_${key}`}
            className={`form-control ${formHook.errors?.associations && formHook.errors?.associations[key] && 'is-invalid'}`}
            ref={formHook.register({ required: true })}
            defaultValue={association.slug}
            onChange={(event) => {
              association.slug = event.target.value
            }}
          />
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (listAssociationsInQueue.length > 0) {
      updateEnabledAddAssociation(false)
      updateEnabledAddAssociationForm(false)
    }
  }, [listAssociationsInQueue])

  return(
    <div
      className={`course_scheduling_associate-layout ${props.className ? props.className : ''}`}
      style={props.style}
      id={props.id}
    >
      {masterCourseScheduling?.status === 'loading' ? (
        <div className="col-md-12 text-center">
          <Spinner className='spinner-loading' animation='border' />
        </div>
      ) : (
        <>
          {masterCourseScheduling?.status === 'success' && masterCourseScheduling?.courseScheduling ? (
           <fieldset disabled={!enabledAddAssociationForm}>
              <form onSubmit={formHook.handleSubmit(handleSubmit)}>
                <div className="row">
                  {customErrors.length > 0 ? (
                    <div className='col-md-12'>
                      <div className="alert alert-danger">
                        {customErrors.map((error) => (
                          <>
                            {<div>{error.message}</div>}
                          </>
                        ))}
                      </div>
                    </div>
                  ): null}
                  <div className="col-md-12">
                    <div className="form-group">
                      <label htmlFor='masterGroup'>
                        {t('modules.courseSchedulingAssociation.masterGroup')}
                        {(masterCourseScheduling?.courseScheduling?.metadata?.service_id) ? ` (${masterCourseScheduling?.courseScheduling?.metadata?.service_id})` : null}
                      </label>
                      <input
                        type="text"
                        name="masterGroup"
                        id="masterGroup"
                        className={`form-control ${formHook.errors.masterGroup && 'is-invalid'}`}
                        ref={formHook.register({ required: true })}
                        defaultValue={masterCourseScheduling?.courseScheduling?.schedulingAssociation?.slug}
                      />
                    </div>
                  </div>
                  {listAssociationsInQueue && listAssociationsInQueue.length > 0 ? (
                    <div className='col-md-12'>
                      <div className='alert alert-info'>
                        <div style={{display: 'flex'}}>
                          <div style={{flex: 1}}>{t('modules.courseSchedulingAssociation.associationsQueue.title')}:</div>
                          <Spinner className='spinner-loading' animation='border' size='sm' />&nbsp;&nbsp;&nbsp;
                        </div>
                        <div>
                          <ul>
                            {listAssociationsInQueue.map((queue) => (
                              <li>
                                {t('modules.courseSchedulingAssociation.associationGroup')}:&nbsp;&nbsp;
                                {queue.slug} ({queue.date})
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ): null}
                  {enabledAddAssociation && (
                    <div className="col-md-12 add-association-section text-right">
                      <div className="form-group">
                        <OverlayTrigger
                          overlay={
                            <Tooltip id={'add_association'} >
                              {t('modules.courseSchedulingAssociation.addAssociation')}
                            </Tooltip>
                          }
                          placement={'top'}
                        >
                          <BsPlusCircleFill
                            color={colors.primary}
                            size={'20px'}
                            onClick={() => handleAddAssociation()}
                            className='icon-scheduling-screen'
                          />
                        </OverlayTrigger>
                      </div>
                    </div>
                  )}
                  {listAssociations.length > 0 ? (
                    <>
                      {listAssociations.map((association: IListAssociations) => (renderAssociation(association)))}
                    </>
                  ): (
                    <div className="col-md-12">
                      <div className='alert alert-warning'>
                        {t('modules.courseSchedulingAssociation.associationsEmpty')}
                      </div>
                    </div>
                  )}
                  {enabledAddAssociation && (
                    <div className="col-12">
                      <button
                        className='btn btn-primary'
                        type='submit'
                        style={{width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}
                        disabled={isLoading}
                      >
                        {isLoading && (
                          <>
                            <Spinner className='spinner-loading' animation='border' size='sm' />&nbsp;&nbsp;&nbsp;
                          </>
                        )}
                        {t('buttons.save')}
                      </button>
                    </div>
                  )}
                </div>
              </form>
           </fieldset>
          ) : (
            <div className='alert alert-danger'>
              {t('modules.courseSchedulingAssociation.masterGroupNotFound')}
            </div>
          )}
        </>
      )}

    </div>
  );
}
export default CourseSchedulingAssociate;
